import React from 'react';
import { Copy } from 'lucide-react';

interface OpeningHours {
  dayOfWeek: number;
  start: string;
  end: string;
  closed?: boolean;
}

interface OpeningHoursTabProps {
  openingHours: OpeningHours[];
  onUpdate: (hours: OpeningHours[]) => void;
}

const DAYS_OF_WEEK = [
  'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 
  'Freitag', 'Samstag', 'Sonntag'
];

export const OpeningHoursTab: React.FC<OpeningHoursTabProps> = ({ openingHours, onUpdate }) => {
  const handleHoursChange = (dayIndex: number, field: keyof OpeningHours, value: any) => {
    const updatedHours = openingHours.map((hours, index) =>
      index === dayIndex 
        ? { 
            ...hours, 
            [field]: value,
            ...(field === 'closed' && value ? { start: '', end: '' } : {})
          } 
        : hours
    );
    onUpdate(updatedHours);
  };

  const copyHoursToAll = (fromDay: number) => {
    const sourceHours = openingHours[fromDay];
    const updatedHours = openingHours.map((hours) => ({
      ...sourceHours,
      dayOfWeek: hours.dayOfWeek
    }));
    onUpdate(updatedHours);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-yellow-50 dark:bg-yellow-900/50 border-l-4 border-yellow-400 dark:border-yellow-500 p-3 sm:p-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-yellow-700 dark:text-yellow-200">
              Tipp: Nutzen Sie den "Übernehmen" Button, um die Öffnungszeiten eines Tages für alle anderen Tage zu übernehmen.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {openingHours.map((hours, index) => (
          <div key={index} className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="w-full sm:w-32">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {DAYS_OF_WEEK[index]}
              </span>
            </div>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hours.closed}
                onChange={(e) => handleHoursChange(index, 'closed', e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Geschlossen</span>
            </label>

            {!hours.closed && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 flex-1">
                <div className="w-full sm:w-auto">
                  <input
                    type="time"
                    value={hours.start}
                    onChange={(e) => handleHoursChange(index, 'start', e.target.value)}
                    className="block w-full sm:w-auto rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
                  />
                </div>
                <span className="hidden sm:block text-gray-500 dark:text-gray-400">bis</span>
                <div className="w-full sm:w-auto">
                  <input
                    type="time"
                    value={hours.end}
                    onChange={(e) => handleHoursChange(index, 'end', e.target.value)}
                    className="block w-full sm:w-auto rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm"
                  />
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => copyHoursToAll(index)}
              className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 w-full sm:w-auto"
            >
              <Copy className="h-4 w-4 mr-1" />
              <span className="sm:hidden">Für alle Tage übernehmen</span>
              <span className="hidden sm:inline">Übernehmen</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};